import { c as _c } from "react/compiler-runtime";
import { Suspense, useEffect, useCallback } from "react";
import type { ReactElement } from "react";
import Sidebar from "./sidebar/Sidebar";
import { OrgUrlSyncProvider } from "./OrgUrlSyncProvider";
import AppSwitcherRail from "./sidebar/AppSwitcherRail";
import { useActiveApp, isCodesmithPath, isSettingsPath, isProofPath, isSandboxesPath, isStandaloneAccountPath, isAdminPath } from "./sidebar/useActiveApp";
import SettingsSidebar from "./sidebar/SettingsSidebar";
import AdminSidebar from "./sidebar/AdminSidebar";
import { useAuth } from "./AuthProvider";
import { useDashboardRouter as useRouter } from "@/hooks/useDashboardRouter";
import SidebarSkeleton from "./sidebar/SidebarSkeleton";
import { MigrationWizardDialog } from "./LazyMigrationWizard";
import { useLocalStorage } from "usehooks-ts";
import { SidebarProvider } from "@/components/ui/sidebar";
import { SidebarMainContent } from "./SidebarMainContent";
import { ShortcutsModal } from "./ShortcutsModal";
import LiveMetricsSheetLoader from "./sidebar/LiveMetricsSheetLoader";
import { useShortcuts } from "@/hooks/useShortcuts";
import { CommandPalette } from "./CommandPalette";
import { useWorkflowCache } from "@/hooks/useWorkflowCache";
import { usePrefetchAssistantSessions } from "@/hooks/usePrefetchAssistantSessions";
import { usePrefetchCodesmithChunk } from "@/hooks/usePrefetchCodesmithChunk";
import { getSidebarStateFromStorage, RailSkeleton, SidebarLayoutSkeleton } from "./SidebarLayoutSkeleton";
export function SidebarLayout(t0) {
  const $ = _c(59);
  const {
    children,
    scrollableMain: t1
  } = t0;
  const scrollableMain = t1 === undefined ? true : t1;
  const [sidebarOpen, setSidebarOpen] = useLocalStorage("sidebar-open", true);
  const [sidebarPreference, setSidebarPreference] = useLocalStorage("sidebar-preference", "auto");
  const {
    shortcutsModalOpen,
    setShortcutsModalOpen
  } = useShortcuts();
  const router = useRouter();
  let t2;
  if ($[0] !== router.pathname) {
    t2 = isCodesmithPath(router.pathname);
    $[0] = router.pathname;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const isCodesmithRoute = t2;
  let t3;
  if ($[2] !== isCodesmithRoute || $[3] !== router.pathname || $[4] !== scrollableMain) {
    t3 = scrollableMain === false || isCodesmithRoute || isProofPath(router.pathname) || isSandboxesPath(router.pathname) || /^(\/\[org\])?\/(usage|settings)(\/|$)/.test(router.pathname) || router.pathname === "/admin" || router.pathname === "/[org]/cache/[repository]";
    $[2] = isCodesmithRoute;
    $[3] = router.pathname;
    $[4] = scrollableMain;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const fixedMain = t3;
  let t4;
  if ($[6] !== router.asPath || $[7] !== router.query.embed) {
    t4 = router.query.embed === "1" || /(?:^|[?&])embed=1(?:&|$)/.test(router.asPath.split("#")[0] ?? "");
    $[6] = router.asPath;
    $[7] = router.query.embed;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const isEmbed = t4;
  const activeApp = useActiveApp();
  let t5;
  if ($[9] !== router.pathname) {
    t5 = isStandaloneAccountPath(router.pathname);
    $[9] = router.pathname;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const isStandalone = t5;
  const showContextualSidebar = !isEmbed && activeApp === "actions" && !isStandalone;
  let t6;
  if ($[11] !== router.pathname) {
    t6 = isSettingsPath(router.pathname);
    $[11] = router.pathname;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const isSettingsRoute = t6;
  const showSettingsSidebar = !isEmbed && isSettingsRoute;
  const {
    user
  } = useAuth();
  let t7;
  if ($[13] !== isEmbed || $[14] !== router.pathname || $[15] !== user?.is_superuser) {
    t7 = !isEmbed && isAdminPath(router.pathname) && !!user?.is_superuser;
    $[13] = isEmbed;
    $[14] = router.pathname;
    $[15] = user?.is_superuser;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  const showAdminSidebar = t7;
  useWorkflowCache();
  usePrefetchAssistantSessions();
  usePrefetchCodesmithChunk();
  let t8;
  if ($[17] !== setSidebarOpen || $[18] !== setSidebarPreference) {
    t8 = open => {
      setSidebarOpen(open);
      setSidebarPreference("manual");
    };
    $[17] = setSidebarOpen;
    $[18] = setSidebarPreference;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  const handleSidebarChange = t8;
  let t10;
  let t9;
  if ($[20] !== setSidebarOpen || $[21] !== sidebarPreference) {
    t9 = () => {
      if (typeof window === "undefined") {
        return;
      }
      const mediaQueryMD = window.matchMedia("(min-width: 768px)");
      const mediaQueryXL = window.matchMedia("(min-width: 1280px)");
      const updateSidebarState = () => {
        const isMD = mediaQueryMD.matches;
        const isXL = mediaQueryXL.matches;
        const shouldOpen = isMD && isXL;
        setSidebarOpen(currentOpen => {
          if (currentOpen !== shouldOpen) {
            return shouldOpen;
          }
          return currentOpen;
        });
      };
      if (sidebarPreference === "auto") {
        updateSidebarState();
      }
      const handleChange = () => {
        if (sidebarPreference === "manual") {
          return;
        }
        updateSidebarState();
      };
      if (mediaQueryMD.addEventListener && mediaQueryXL.addEventListener) {
        mediaQueryMD.addEventListener("change", handleChange);
        mediaQueryXL.addEventListener("change", handleChange);
        return () => {
          mediaQueryMD.removeEventListener("change", handleChange);
          mediaQueryXL.removeEventListener("change", handleChange);
        };
      } else {
        mediaQueryMD.addListener(handleChange);
        mediaQueryXL.addListener(handleChange);
        return () => {
          mediaQueryMD.removeListener(handleChange);
          mediaQueryXL.removeListener(handleChange);
        };
      }
    };
    t10 = [sidebarPreference, setSidebarOpen];
    $[20] = setSidebarOpen;
    $[21] = sidebarPreference;
    $[22] = t10;
    $[23] = t9;
  } else {
    t10 = $[22];
    t9 = $[23];
  }
  useEffect(t9, t10);
  let t11;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <SidebarLayoutSkeleton />;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  const t12 = showContextualSidebar || showSettingsSidebar || showAdminSidebar;
  let t13;
  if ($[25] !== isEmbed) {
    t13 = !isEmbed && <Suspense fallback={<RailSkeleton />}><AppSwitcherRail /></Suspense>;
    $[25] = isEmbed;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  let t14;
  if ($[27] !== showContextualSidebar) {
    t14 = showContextualSidebar && <Suspense fallback={<SidebarSkeleton collapsed={!getSidebarStateFromStorage()} />}><Sidebar /></Suspense>;
    $[27] = showContextualSidebar;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  let t15;
  if ($[29] !== showSettingsSidebar) {
    t15 = showSettingsSidebar && <Suspense fallback={<SidebarSkeleton collapsed={!getSidebarStateFromStorage()} standalone={true} />}><SettingsSidebar /></Suspense>;
    $[29] = showSettingsSidebar;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  let t16;
  if ($[31] !== showAdminSidebar) {
    t16 = showAdminSidebar && <Suspense fallback={<SidebarSkeleton collapsed={!getSidebarStateFromStorage()} standalone={true} />}><AdminSidebar /></Suspense>;
    $[31] = showAdminSidebar;
    $[32] = t16;
  } else {
    t16 = $[32];
  }
  const t17 = isCodesmithRoute ? "codesmith" : isSettingsRoute ? "settings" : router.pathname;
  let t18;
  if ($[33] !== children || $[34] !== fixedMain || $[35] !== t17) {
    t18 = <SidebarMainContent scrollResetKey={t17} fixedMain={fixedMain}>{children}</SidebarMainContent>;
    $[33] = children;
    $[34] = fixedMain;
    $[35] = t17;
    $[36] = t18;
  } else {
    t18 = $[36];
  }
  let t19;
  if ($[37] !== t13 || $[38] !== t14 || $[39] !== t15 || $[40] !== t16 || $[41] !== t18) {
    t19 = <div className="flex-1 flex overflow-hidden">{t13}{t14}{t15}{t16}{t18}</div>;
    $[37] = t13;
    $[38] = t14;
    $[39] = t15;
    $[40] = t16;
    $[41] = t18;
    $[42] = t19;
  } else {
    t19 = $[42];
  }
  let t20;
  if ($[43] !== handleSidebarChange || $[44] !== sidebarOpen || $[45] !== t12 || $[46] !== t19) {
    t20 = <SidebarProvider open={sidebarOpen} onOpenChange={handleSidebarChange} enableKeyboardShortcut={t12} className="flex-1 min-h-0 relative">{t19}</SidebarProvider>;
    $[43] = handleSidebarChange;
    $[44] = sidebarOpen;
    $[45] = t12;
    $[46] = t19;
    $[47] = t20;
  } else {
    t20 = $[47];
  }
  let t21;
  if ($[48] !== setShortcutsModalOpen || $[49] !== shortcutsModalOpen) {
    t21 = <ShortcutsModal open={shortcutsModalOpen} onOpenChange={setShortcutsModalOpen} />;
    $[48] = setShortcutsModalOpen;
    $[49] = shortcutsModalOpen;
    $[50] = t21;
  } else {
    t21 = $[50];
  }
  let t22;
  if ($[51] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <CommandPalette />;
    $[51] = t22;
  } else {
    t22 = $[51];
  }
  let t23;
  if ($[52] !== isEmbed) {
    t23 = !isEmbed && <MigrationWizardDialog />;
    $[52] = isEmbed;
    $[53] = t23;
  } else {
    t23 = $[53];
  }
  let t24;
  if ($[54] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = <LiveMetricsSheetLoader />;
    $[54] = t24;
  } else {
    t24 = $[54];
  }
  let t25;
  if ($[55] !== t20 || $[56] !== t21 || $[57] !== t23) {
    t25 = <Suspense fallback={t11}><div className="flex flex-col h-dvh">{t20}{t21}{t22}{t23}{t24}</div></Suspense>;
    $[55] = t20;
    $[56] = t21;
    $[57] = t23;
    $[58] = t25;
  } else {
    t25 = $[58];
  }
  return t25;
}

// Every page shares these helpers so the OrgUrlSyncProvider > SidebarLayout
// element shape is identical across routes and React reconciles the persistent
// chrome instead of remounting it. useOrgUrlSync is a no-op on routes without
// a URL org and skips /admin/internal-monitors, so mounting it globally is safe.
export function getSidebarLayout(page: ReactElement) {
  return <OrgUrlSyncProvider>
      <SidebarLayout>{page}</SidebarLayout>
    </OrgUrlSyncProvider>;
}

// For pages whose content owns its scroll (scrollableMain={false}).
export function getFixedSidebarLayout(page: ReactElement) {
  return <OrgUrlSyncProvider>
      <SidebarLayout scrollableMain={false}>{page}</SidebarLayout>
    </OrgUrlSyncProvider>;
}